import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarDays,
  CheckCircle2,
  Loader2,
  Power,
  PowerOff,
  Save,
  Sparkles,
  Zap,
} from "lucide-react";
import API from "../../../services/api";
import {
  fetchWfSetupOptions,
  fetchWfSetupStatus,
  fetchWfAttendanceProfile,
  updateAttendanceConfiguration,
  updateWorkScheduleConfiguration,
} from "../../../Moduels/attendance/wfApi";
import { useSetupProgress } from "../../../Moduels/Context/SetupProgressContext";
import { OnboardingContext } from "../../../Moduels/Context/OnboardingContext";
import {
  ATTENDANCE_METHOD_META,
  ROSTER_CONFIG_META,
  WORK_SCHEDULE_META,
  formatAttendanceSourceLabel,
  formatWorkScheduleSummary,
  readWorkSchedule,
  readWorkingDays,
  resolveCurrentSource,
} from "./attendanceConfigurationMeta";
import BrixigoPageLoader from "../../common/BrixigoPageLoader";
import SuccessCelebration from "../../ui/SuccessCelebration";
import { useToast } from "../../../Moduels/Context/ToastContext";
import "./attendance-configuration.css";

function formatApiError(err, fallback = "Something went wrong") {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => (typeof item === "string" ? item : item?.msg || JSON.stringify(item))).join("; ");
  }
  if (typeof detail === "object") return detail.message || detail.msg || JSON.stringify(detail);
  return fallback;
}

const STEPS = [
  { n: 1, title: "Current setup", key: "review" },
  { n: 2, title: "Attendance source", key: "source" },
  { n: 3, title: "Work schedule", key: "schedule" },
  { n: 4, title: "Working days", key: "days" },
];

export default function AttendanceConfigurationPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const setupProgressCtx = useSetupProgress();
  const onboardingCtx = useContext(OnboardingContext);
  const welcome = searchParams.get("welcome") === "1";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState("");
  const [savedTitle, setSavedTitle] = useState("Updated successfully");
  const [err, setErr] = useState("");
  const [step, setStep] = useState(1);

  const [status, setStatus] = useState(null);
  const [options, setOptions] = useState(null);
  const [profile, setProfile] = useState(null);

  const [savedSource, setSavedSource] = useState("");
  const [selectedSource, setSelectedSource] = useState("");
  const [savedWorkingDays, setSavedWorkingDays] = useState(26);
  const [workingDays, setWorkingDays] = useState("26");

  const [savedSchedule, setSavedSchedule] = useState({
    work_schedule_type: "fixed_schedule",
    enable_shift_roster: false,
    roster_config: [],
  });
  const [selectedScheduleType, setSelectedScheduleType] = useState("fixed_schedule");
  const [enableShiftRoster, setEnableShiftRoster] = useState(false);
  const [rosterConfig, setRosterConfig] = useState([]);

  const load = useCallback(async () => {
    setErr("");
    try {
      const [st, opts, setupRes, hrRes, profile] = await Promise.all([
        fetchWfSetupStatus(),
        fetchWfSetupOptions(),
        API.get("/setup/status"),
        API.get("/organisation/me/hr-settings"),
        fetchWfAttendanceProfile(),
      ]);
      const org = setupRes?.data?.org || {};
      const payroll = hrRes?.data?.payroll_settings || hrRes?.data?.hr_settings?.payroll || {};
      const hrAtt = org.attendance_defaults || hrRes?.data?.hr_settings?.attendance || {};

      if (st.setup_required && !st.setup_completed_at) {
        navigate("/attendance/setup", { replace: true });
        return;
      }

      const current = resolveCurrentSource(
        {
          ...st,
          enabled_modes: profile?.enabled_modes,
          default_source: profile?.default_source,
        },
        hrAtt,
        profile
      );
      const days = readWorkingDays(org, payroll);
      const ws = readWorkSchedule(profile, hrAtt);

      setStatus(st);
      setOptions(opts);
      setProfile(profile);
      setSavedSource(current);
      setSelectedSource(current);
      setSavedWorkingDays(days);
      setWorkingDays(String(days));
      setSavedSchedule(ws);
      setSelectedScheduleType(ws.work_schedule_type);
      setEnableShiftRoster(ws.enable_shift_roster);
      setRosterConfig(Array.isArray(ws.roster_config) ? ws.roster_config : []);
    } catch (e) {
      setErr(formatApiError(e, "Failed to load attendance configuration"));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load().then(() => {
      if (searchParams.get("welcome") === "1") {
        setStep(4);
      }
    });
  }, [load, searchParams]);

  const sourceOptions = options?.sources || [];
  const workScheduleOptions = options?.work_schedules || Object.keys(WORK_SCHEDULE_META).map((code) => ({
    code,
    label: WORK_SCHEDULE_META[code].label,
    description: WORK_SCHEDULE_META[code].short,
  }));
  const rosterConfigOptions = options?.roster_config || Object.keys(ROSTER_CONFIG_META).map((code) => ({
    code,
    label: ROSTER_CONFIG_META[code].label,
  }));
  const selectedMeta = ATTENDANCE_METHOD_META[selectedSource] || null;
  const savedMeta = ATTENDANCE_METHOD_META[savedSource] || null;
  const SavedIcon = savedMeta?.icon;

  const methodChanged = selectedSource && selectedSource !== savedSource;
  const scheduleChanged =
    selectedScheduleType !== savedSchedule.work_schedule_type ||
    enableShiftRoster !== savedSchedule.enable_shift_roster ||
    JSON.stringify([...rosterConfig].sort()) !== JSON.stringify([...(savedSchedule.roster_config || [])].sort());
  const daysNum = Number(workingDays);
  const daysValid = Number.isFinite(daysNum) && daysNum >= 1 && daysNum <= 31;
  const daysChanged = daysValid && daysNum !== savedWorkingDays;
  const isDirty = methodChanged || scheduleChanged || daysChanged;

  const rosterIsActive = Boolean(
    savedSchedule.enable_shift_roster ||
      ["shift_roster", "rotational_shift", "weekly_shift_pattern", "open_shift"].includes(
        savedSchedule.work_schedule_type
      )
  );

  const persistWorkSchedule = async (payload, flashMessage) => {
    setErr("");
    setSavedFlash("");
    setSaving(true);
    try {
      await updateWorkScheduleConfiguration(payload);
      await load();
      if (setupProgressCtx?.refetch) await setupProgressCtx.refetch();
      window.dispatchEvent(new CustomEvent("attendance-profile-changed"));
      setSavedFlash(flashMessage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setErr(formatApiError(e, "Failed to save work schedule"));
    } finally {
      setSaving(false);
    }
  };

  const disableShiftRosterNow = () =>
    persistWorkSchedule(
      {
        work_schedule_type: "fixed_schedule",
        enable_shift_roster: false,
        roster_config: savedSchedule.roster_config || rosterConfig || [],
      },
      "Shift roster disabled. Attendance source unchanged — roster plans and shifts are kept for when you re-enable."
    );

  const enableShiftRosterNow = () =>
    persistWorkSchedule(
      {
        work_schedule_type: selectedScheduleType === "fixed_schedule" ? "shift_roster" : selectedScheduleType,
        enable_shift_roster: true,
        roster_config:
          rosterConfig.length > 0
            ? rosterConfig
            : savedSchedule.roster_config?.length
              ? savedSchedule.roster_config
              : ["weekly_roster", "monthly_roster"],
      },
      "Shift roster enabled. Published roster assignments will be used during attendance calculation."
    );

  const saveWorkScheduleStep = async () => {
    if (!selectedSource) {
      setErr("Select an attendance source first (step 2).");
      setStep(2);
      return;
    }
    await persistWorkSchedule(
      {
        work_schedule_type: enableShiftRoster ? selectedScheduleType : "fixed_schedule",
        enable_shift_roster: enableShiftRoster,
        roster_config: enableShiftRoster ? rosterConfig : savedSchedule.roster_config || [],
      },
      enableShiftRoster
        ? "Work schedule saved. Shift roster is active in the attendance engine."
        : "Work schedule saved. Shift roster is disabled — using fixed schedule only."
    );
  };

  const exampleSalary = useMemo(() => {
    if (!daysValid) return null;
    const gross = 42000;
    const daily = gross / daysNum;
    return { gross, daily: daily.toFixed(0), days: daysNum };
  }, [daysNum, daysValid]);

  const dismissWelcome = () => {
    searchParams.delete("welcome");
    setSearchParams(searchParams, { replace: true });
  };

  const finishAndGoDashboard = async (title, msg) => {
    setSavedTitle(title);
    setSavedFlash(msg);
    toast.success(`${msg} Redirecting to dashboard…`, { title });
    dismissWelcome();
    if (setupProgressCtx?.refetch) await setupProgressCtx.refetch();
    if (onboardingCtx?.refetch) await onboardingCtx.refetch();
    window.dispatchEvent(new CustomEvent("attendance-profile-changed"));
    window.setTimeout(() => navigate("/dashboard"), 1200);
  };

  const saveAll = async () => {
    setErr("");
    setSavedFlash("");
    if (!selectedSource) {
      setErr("Select an attendance capture method before finishing setup.");
      setStep(2);
      return;
    }
    if (!daysValid) {
      setErr("Working days must be between 1 and 31. Enter a valid day count to complete attendance setup.");
      setStep(4);
      return;
    }

    setSaving(true);
    try {
      // Always persist capture method so onboarding marks attendance complete
      // (setup_completed_at is set by update_attendance_configuration).
      if (methodChanged || !status?.setup_completed_at) {
        await updateAttendanceConfiguration(selectedSource);
      }

      if (scheduleChanged) {
        await updateWorkScheduleConfiguration({
          work_schedule_type: enableShiftRoster ? selectedScheduleType : "fixed_schedule",
          enable_shift_roster: enableShiftRoster,
          roster_config: enableShiftRoster ? rosterConfig : savedSchedule.roster_config || [],
        });
      }

      if (daysChanged || methodChanged || !status?.setup_completed_at) {
        await API.patch("/organisation/me/hr-settings", {
          payroll: {
            monthly_salary_divisor_days: daysNum,
            total_working_days_mode: "fixed",
          },
        });
      }

      setSavedSource(selectedSource);
      setSavedWorkingDays(daysNum);
      await load();

      const msg =
        methodChanged && scheduleChanged
          ? "Attendance setup complete. Source and work schedule are active organisation-wide."
          : methodChanged
            ? "Attendance setup complete. Capture method is active organisation-wide."
            : scheduleChanged
              ? enableShiftRoster
                ? "Attendance setup complete. Shift roster now participates in attendance calculation."
                : "Attendance setup complete. Work schedule saved."
              : "Attendance setup complete. Working days and capture method are active organisation-wide.";
      await finishAndGoDashboard("Successfully completed", msg);
    } catch (e) {
      setErr(formatApiError(e, "Failed to save changes"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <BrixigoPageLoader message="Loading attendance configuration…" />;
  }

  return (
    <div className="att-cfg-root">
      <motion.header
        className="att-cfg-hero"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="att-cfg-hero-inner">
          <div className="att-cfg-eyebrow">
            <Sparkles className="h-3.5 w-3.5" />
            Attendance configuration
          </div>
          <h1>Attendance settings</h1>
          <p>
            Configure how attendance is captured, how shifts are scheduled, and how salary divides
            across working days — each layer is independent and can be changed anytime.
          </p>
        </div>
      </motion.header>

      <div className="att-cfg-steps">
        {STEPS.map((s) => (
          <button
            key={s.key}
            type="button"
            className={`att-cfg-step ${step === s.n ? "att-cfg-step--active" : step > s.n ? "att-cfg-step--done" : ""}`}
            onClick={() => setStep(s.n)}
          >
            {step > s.n ? "✓ " : ""}
            {s.n}. {s.title}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {welcome && (
          <motion.div
            key="welcome"
            className="att-cfg-banner att-cfg-banner--success"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
          >
            <p className="font-semibold">Attendance method activated — one more step</p>
            <p className="mt-1">
              Confirm your working days below. This number divides monthly gross salary for payroll and
              loss-of-pay calculations.
            </p>
          </motion.div>
        )}
        {savedFlash && (
          <SuccessCelebration
            key="saved"
            title={savedTitle}
            message={savedFlash}
            hint="Your organisation-wide attendance settings are now active."
            onDismiss={() => setSavedFlash("")}
            autoDismissMs={8000}
          />
        )}
        {err && (
          <motion.div
            key="err"
            className="att-cfg-banner att-cfg-banner--warn"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            data-auto-scroll-alert="true"
            role="alert"
          >
            {err}
          </motion.div>
        )}
      </AnimatePresence>

      {isDirty && (
        <div className="att-cfg-dirty">
          <Zap className="h-3.5 w-3.5" />
          Unsaved changes — save to apply across the system
        </div>
      )}

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.22 }}
          >
            <div className="att-cfg-card">
              <div className="att-cfg-card-body">
                <p className="att-cfg-card-title">Your current setup</p>
                <p className="att-cfg-card-sub">These settings are active for attendance and payroll right now.</p>

                <div className="att-cfg-current">
                  {SavedIcon ? (
                    <div className={`att-cfg-current-icon ${savedMeta?.accent || "sky"}`}>
                      <SavedIcon className="h-5 w-5" />
                    </div>
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <div className="att-cfg-current-label">Attendance source</div>
                    <div className="att-cfg-current-value">
                      {formatAttendanceSourceLabel(savedSource, profile)}
                    </div>
                    <div className="att-cfg-current-meta">{savedMeta?.short || "Choose a source in step 2"}</div>
                  </div>
                  <div className="text-right min-w-[140px]">
                    <div className="att-cfg-current-label">Work schedule</div>
                    <div className="att-cfg-current-value">{formatWorkScheduleSummary(profile || savedSchedule)}</div>
                    <div className="att-cfg-current-meta">
                      {rosterIsActive ? "Shift roster active" : "Fixed / calendar only"}
                    </div>
                  </div>
                  <div className="text-right min-w-[100px]">
                    <div className="att-cfg-current-label">Working days</div>
                    <div className="att-cfg-current-value">{savedWorkingDays}</div>
                    <div className="att-cfg-current-meta">Salary divisor</div>
                  </div>
                </div>

                {savedMeta?.tips?.length ? (
                  <div className="att-cfg-tips">
                    <div className="att-cfg-tips-title">Quick tips for {savedMeta.label}</div>
                    <ul>
                      {savedMeta.tips.map((t) => (
                        <li key={t}>{t}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {rosterIsActive ? (
                  <div className="att-cfg-roster-status att-cfg-roster-status--on">
                    <span className="att-cfg-roster-pill att-cfg-roster-pill--on">Shift roster ON</span>
                    <p className="text-sm text-slate-600 mt-2">
                      Roster assignments are used during attendance calculation. Your capture source (
                      {formatAttendanceSourceLabel(savedSource, profile)}) is unchanged.
                    </p>
                    <button
                      type="button"
                      className="att-cfg-btn att-cfg-btn--danger mt-3"
                      disabled={saving}
                      onClick={disableShiftRosterNow}
                    >
                      {saving ? (
                        <>
                          <Loader2 className="inline h-4 w-4 animate-spin mr-1" />
                          Disabling…
                        </>
                      ) : (
                        <>
                          <PowerOff className="inline h-4 w-4 mr-1" />
                          Disable shift roster
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="att-cfg-roster-status att-cfg-roster-status--off">
                    <span className="att-cfg-roster-pill att-cfg-roster-pill--off">Shift roster OFF</span>
                    <p className="text-sm text-slate-500 mt-2">
                      Enable shift roster under Work Schedule (step 3) when you need per-day shift assignments.
                    </p>
                  </div>
                )}

                <div className="att-cfg-actions" style={{ marginTop: 18 }}>
                  <button type="button" className="att-cfg-btn att-cfg-btn--primary" onClick={() => setStep(2)}>
                    Change method or days →
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div
            key="step2"
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.22 }}
          >
            <div className="att-cfg-card">
              <div className="att-cfg-card-body">
                <p className="att-cfg-card-title">Attendance source</p>
                <p className="att-cfg-card-sub">
                  How punches or marks enter the system. Shift roster is not an attendance source —
                  enable it under Work Schedule (step 3).
                </p>

                <div className="att-cfg-methods">
                  {sourceOptions.map((o) => {
                    const meta = ATTENDANCE_METHOD_META[o.code] || {};
                    const Icon = meta.icon || CheckCircle2;
                    const selected = selectedSource === o.code;
                    return (
                      <label
                        key={o.code}
                        className={`att-cfg-method ${selected ? "att-cfg-method--selected" : ""}`}
                      >
                        <input
                          type="radio"
                          name="attendance_source"
                          checked={selected}
                          onChange={() => setSelectedSource(o.code)}
                        />
                        <div className="att-cfg-method-icon">
                          <Icon className="h-4 w-4" />
                        </div>
                        <div>
                          <strong>{o.label || meta.label}</strong>
                          <span>{o.description || meta.description}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>

                {selectedMeta?.tips?.length ? (
                  <div className="att-cfg-tips">
                    <div className="att-cfg-tips-title">What happens with {selectedMeta.label}</div>
                    <ul>
                      {selectedMeta.tips.map((t) => (
                        <li key={t}>{t}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {methodChanged && (
                  <div className="att-cfg-banner att-cfg-banner--info" style={{ marginTop: 14 }}>
                    Switching from <strong>{savedMeta?.label || savedSource}</strong> to{" "}
                    <strong>{selectedMeta?.label || selectedSource}</strong> will reconfigure attendance
                    across the system when you save.
                  </div>
                )}
              </div>
            </div>

            <div className="att-cfg-actions">
              <button type="button" className="att-cfg-btn att-cfg-btn--ghost" onClick={() => setStep(1)}>
                Back
              </button>
              <button
                type="button"
                className="att-cfg-btn att-cfg-btn--primary"
                disabled={!selectedSource}
                onClick={() => setStep(3)}
              >
                Continue to work schedule →
              </button>
            </div>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div
            key="step3"
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.22 }}
          >
            <div className="att-cfg-card">
              <div className="att-cfg-card-body">
                <p className="att-cfg-card-title">Work schedule</p>
                <p className="att-cfg-card-sub">
                  Scheduling layer used during attendance calculation. Enable or disable shift roster anytime —
                  your attendance source and roster data stay unchanged.
                </p>

                <div className="att-cfg-roster-toggle">
                  <button
                    type="button"
                    className={`att-cfg-roster-toggle-btn ${enableShiftRoster ? "is-active" : ""}`}
                    onClick={() => {
                      setEnableShiftRoster(true);
                      if (selectedScheduleType === "fixed_schedule") {
                        setSelectedScheduleType("shift_roster");
                      }
                    }}
                  >
                    <Power className="h-4 w-4" />
                    Enable
                  </button>
                  <button
                    type="button"
                    className={`att-cfg-roster-toggle-btn ${!enableShiftRoster ? "is-active is-off" : ""}`}
                    onClick={() => {
                      setEnableShiftRoster(false);
                      setSelectedScheduleType("fixed_schedule");
                    }}
                  >
                    <PowerOff className="h-4 w-4" />
                    Disable
                  </button>
                </div>

                <p className="att-cfg-hint" style={{ marginTop: 10 }}>
                  Status:{" "}
                  <strong>{enableShiftRoster ? "Shift roster enabled" : "Shift roster disabled"}</strong>
                  {enableShiftRoster !== savedSchedule.enable_shift_roster ? " (unsaved)" : ""}
                </p>

                {enableShiftRoster ? (
                  <>
                    <div className="att-cfg-methods" style={{ marginTop: 16 }}>
                      {workScheduleOptions.map((o) => {
                        const meta = WORK_SCHEDULE_META[o.code] || {};
                        const Icon = meta.icon || CalendarDays;
                        const selected = selectedScheduleType === o.code;
                        return (
                          <label
                            key={o.code}
                            className={`att-cfg-method ${selected ? "att-cfg-method--selected" : ""}`}
                          >
                            <input
                              type="radio"
                              name="work_schedule_type"
                              checked={selected}
                              onChange={() => setSelectedScheduleType(o.code)}
                            />
                            <div className="att-cfg-method-icon">
                              <Icon className="h-4 w-4" />
                            </div>
                            <div>
                              <strong>{o.label || meta.label}</strong>
                              <span>{o.description || meta.short}</span>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="att-cfg-banner att-cfg-banner--info" style={{ marginTop: 14 }}>
                    Shift roster is off. Attendance uses fixed schedule / org defaults only. Roster
                    board data is kept — re-enable anytime.
                  </div>
                )}
              </div>
            </div>

            {enableShiftRoster && (
              <div className="att-cfg-card">
                <div className="att-cfg-card-body">
                  <p className="att-cfg-card-title">Roster configuration</p>
                  <p className="att-cfg-card-sub">Select roster planning modes available to HR.</p>
                  <div className="att-cfg-methods">
                    {rosterConfigOptions.map((o) => {
                      const checked = rosterConfig.includes(o.code);
                      return (
                        <label
                          key={o.code}
                          className={`att-cfg-method ${checked ? "att-cfg-method--selected" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setRosterConfig((prev) =>
                                checked ? prev.filter((c) => c !== o.code) : [...prev, o.code]
                              );
                            }}
                          />
                          <div>
                            <strong>{o.label}</strong>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {scheduleChanged && (
              <div className="att-cfg-banner att-cfg-banner--info" style={{ marginTop: 14 }}>
                Work schedule changes apply to attendance calculation immediately. Attendance source
                ({formatAttendanceSourceLabel(savedSource, profile)}) stays unchanged.
              </div>
            )}

            <div className="att-cfg-actions">
              <button type="button" className="att-cfg-btn att-cfg-btn--ghost" onClick={() => setStep(2)}>
                Back
              </button>
              {rosterIsActive ? (
                <button
                  type="button"
                  className="att-cfg-btn att-cfg-btn--danger"
                  disabled={saving}
                  onClick={disableShiftRosterNow}
                >
                  {saving ? (
                    <>
                      <Loader2 className="inline h-4 w-4 animate-spin mr-1" />
                      Disabling…
                    </>
                  ) : (
                    <>
                      <PowerOff className="inline h-4 w-4 mr-1" />
                      Disable shift roster
                    </>
                  )}
                </button>
              ) : enableShiftRoster ? (
                <button
                  type="button"
                  className="att-cfg-btn att-cfg-btn--primary"
                  disabled={saving}
                  onClick={enableShiftRosterNow}
                >
                  {saving ? (
                    <>
                      <Loader2 className="inline h-4 w-4 animate-spin mr-1" />
                      Enabling…
                    </>
                  ) : (
                    <>
                      <Power className="inline h-4 w-4 mr-1" />
                      Enable &amp; save
                    </>
                  )}
                </button>
              ) : null}
              {scheduleChanged && !enableShiftRoster && !rosterIsActive ? (
                <button
                  type="button"
                  className="att-cfg-btn att-cfg-btn--primary"
                  disabled={saving}
                  onClick={saveWorkScheduleStep}
                >
                  {saving ? (
                    <>
                      <Loader2 className="inline h-4 w-4 animate-spin mr-1" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <Save className="inline h-4 w-4 mr-1" />
                      Save work schedule
                    </>
                  )}
                </button>
              ) : null}
              {scheduleChanged && enableShiftRoster && rosterIsActive ? (
                <button
                  type="button"
                  className="att-cfg-btn att-cfg-btn--primary"
                  disabled={saving}
                  onClick={saveWorkScheduleStep}
                >
                  {saving ? (
                    <>
                      <Loader2 className="inline h-4 w-4 animate-spin mr-1" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <Save className="inline h-4 w-4 mr-1" />
                      Save work schedule
                    </>
                  )}
                </button>
              ) : null}
              <button
                type="button"
                className="att-cfg-btn att-cfg-btn--primary"
                onClick={() => setStep(4)}
              >
                Continue to working days →
              </button>
            </div>
          </motion.div>
        )}

        {step === 4 && (
          <motion.div
            key="step4"
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.22 }}
          >
            <div className="att-cfg-card att-cfg-card--salary">
              <div className="att-cfg-card-body">
                <div className="att-cfg-salary-hd">
                  <div className="att-cfg-salary-icon">
                    <CalendarDays className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="att-cfg-card-title">Working days for salary</p>
                    <p className="att-cfg-card-sub" style={{ marginBottom: 0 }}>
                      Fixed days used to divide monthly gross salary. Common values: 21 (manufacturing), 26
                      (standard), or 30 (calendar month).
                    </p>
                  </div>
                </div>

                <div className="att-cfg-days-row">
                  <div className="att-cfg-days-field">
                    <label className="att-cfg-current-label" htmlFor="working-days">
                      Payable / working days
                    </label>
                    <input
                      id="working-days"
                      className={`att-cfg-input att-cfg-input--highlight ${!daysValid && workingDays !== "" ? "err" : ""}`}
                      type="number"
                      min={1}
                      max={31}
                      value={workingDays}
                      onChange={(e) => setWorkingDays(e.target.value)}
                    />
                    <p className="att-cfg-hint">Used as: monthly gross ÷ working days = daily rate</p>
                  </div>
                  <div>
                    {exampleSalary && (
                      <div className="att-cfg-example att-cfg-example--vivid">
                        <div className="att-cfg-example-title">
                          <CalendarDays className="h-4 w-4" />
                          Example with ₹{exampleSalary.gross.toLocaleString("en-IN")} gross
                        </div>
                        <p className="att-cfg-example-calc">
                          ₹{exampleSalary.gross.toLocaleString("en-IN")} ÷ {exampleSalary.days} days ={" "}
                          <strong>₹{exampleSalary.daily}/day</strong>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="att-cfg-card att-cfg-card--summary">
              <div className="att-cfg-card-body">
                <p className="att-cfg-card-title">Summary before save</p>
                <dl className="att-cfg-summary-list">
                  <div className="att-cfg-summary-row">
                    <dt>Attendance source</dt>
                    <dd>
                      {selectedMeta?.label || selectedSource || "—"}
                      {methodChanged ? <span className="att-cfg-tag att-cfg-tag--change">changed</span> : null}
                    </dd>
                  </div>
                  <div className="att-cfg-summary-row">
                    <dt>Work schedule</dt>
                    <dd>
                      {WORK_SCHEDULE_META[selectedScheduleType]?.label || selectedScheduleType}
                      {enableShiftRoster ? " + Roster" : ""}
                      {scheduleChanged ? <span className="att-cfg-tag att-cfg-tag--change">changed</span> : null}
                    </dd>
                  </div>
                  <div className="att-cfg-summary-row att-cfg-summary-row--last">
                    <dt>Working days</dt>
                    <dd>
                      {daysValid ? daysNum : "—"}
                      {daysChanged ? <span className="att-cfg-tag att-cfg-tag--change">changed</span> : null}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            <div className="att-cfg-save-bar">
              {welcome && !isDirty && (
                <p className="att-cfg-save-hint">
                  <CheckCircle2 className="h-4 w-4" />
                  Review the summary, then save to finish setup.
                </p>
              )}
              <div className="att-cfg-actions att-cfg-actions--bar">
                <button type="button" className="att-cfg-btn att-cfg-btn--ghost" onClick={() => setStep(3)}>
                  Back
                </button>
                <button
                  type="button"
                  className="att-cfg-btn att-cfg-btn--primary att-cfg-btn--save"
                  disabled={saving || !daysValid || !selectedSource}
                  onClick={saveAll}
                >
                  {saving ? (
                    <>
                      <Loader2 className="inline h-4 w-4 animate-spin mr-1" />
                      Applying…
                    </>
                  ) : (
                    <>
                      <Save className="inline h-4 w-4 mr-1" />
                      {welcome && !isDirty ? "Confirm & apply organisation-wide" : "Save & apply organisation-wide"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!welcome && status?.setup_completed_at && (
        <p className="text-center text-xs text-slate-400 mt-6">
          Need module tasks?{" "}
          <Link to="/attendance/onboarding" className="text-sky-600 hover:underline">
            Open module configuration
          </Link>
          {" · "}
          <button
            type="button"
            className="text-sky-600 hover:underline"
            onClick={() => navigate("/attendance")}
          >
            Go to attendance home
          </button>
        </p>
      )}
    </div>
  );
}
